import { Droplet, Layers, Atom, FlaskConical, Pill, Microscope, Heart, ShieldCheck, Zap, Salad, Leaf, Home as HomeIcon } from "lucide-react";
import PageHero, { IconList, SplitSection } from "@/components/site/PageHero";
import { Btn, Reveal } from "@/components/site/Reveal";

const CONTAMINANTS = [
  { icon: Droplet, title: "Chlorine & Chloramine", text: "Added for disinfection, can affect taste, odor, and overall health" },
  { icon: Layers, title: "Sediment", text: "Dirt, sand, and rust from aging pipes and infrastructure" },
  { icon: Atom, title: "VOCs", text: "Volatile organic compounds from industrial and environmental sources" },
  { icon: FlaskConical, title: "Heavy Metals", text: "Such as lead, arsenic, and mercury from natural sources and old pipes" },
  { icon: Pill, title: "Pharmaceuticals", text: "Trace amounts of medications and personal care products" },
  { icon: Microscope, title: "Microplastics", text: "Small plastic particles found in tap water" },
];

const BENEFITS = [
  { icon: Heart, title: "Better hydration", text: "Helps your body perform at its best" },
  { icon: Salad, title: "Improved digestion", text: "Cleaner water is easier on your body" },
  { icon: ShieldCheck, title: "Supports immune health", text: "Fewer harmful chemicals in your water" },
  { icon: Leaf, title: "Healthier skin and hair", text: "Reduced exposure to harsh chemicals" },
  { icon: Zap, title: "More energy", text: "Proper hydration helps you feel better and stay active" },
  { icon: HomeIcon, title: "A healthier home", text: "Clean water for drinking, cooking, and everyday use" },
];

export default function Learn() {
  return (
    <>
      <PageHero
        testId="learn-hero"
        eyebrow="Know What You're Drinking"
        lines={["Learn", "about", "your water."]}
        text="Your water might look clean, but it can still contain harmful contaminants. Learn what's in San Diego's water, how it can affect your health, and how a water purification system can make a difference for you and your family."
        img="/img/water-glass.jpg"
        alt="Glass of water on a marble counter"
      />
      <SplitSection
        testId="learn-source"
        reverse
        eyebrow="San Diego Water"
        title={<>Where your<br />water comes from.</>}
        text="San Diego's water comes from a mix of local sources, imported water, and recycled water. While it meets federal safety standards, it still goes through extensive treatment with chemicals and can pick up contaminants along the way through aging infrastructure and the environment."
        img="/img/sd-bay.jpg"
        alt="San Diego skyline across the bay"
      />
      <section data-testid="learn-contaminants" className="bg-[var(--soft)] py-16 lg:py-20">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow">Common Contaminants</p>
            <h2 data-testid="learn-contaminants-title" className="headline mt-4 text-4xl sm:text-5xl">What can be in<br />your water?</h2>
            <p className="mt-5 text-[17px] text-[var(--body)] leading-relaxed">Even treated water can contain contaminants that affect taste, odor, and your health. Some of the most common include:</p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7 lg:border-l border-[#D4D4D4] lg:pl-10">
            <IconList items={CONTAMINANTS} cols={2} testId="contaminant-list" />
          </Reveal>
        </div>
      </section>
      <SplitSection
        testId="learn-health"
        eyebrow="The Impact on Your Health"
        title={<>Cleaner water<br />a healthier you.</>}
        text="Clean, purified water plays a major role in your overall health and wellness. By reducing harmful contaminants, you can enjoy better tasting water and support:"
        img="/img/kid.jpg"
        alt="Child drinking a glass of water in a bright kitchen"
      >
        <div className="mt-8"><IconList items={BENEFITS} cols={2} testId="benefit-list" /></div>
      </SplitSection>
      <section data-testid="learn-cta" className="bg-white border-t border-[var(--line)] py-14">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-8 items-center">
          <Reveal><p className="eyebrow">Take Control</p><h2 className="headline mt-3 text-4xl sm:text-5xl">Invest in cleaner,<br />healthier water.</h2></Reveal>
          <Reveal delay={0.1} className="lg:border-l border-[var(--line)] lg:pl-10">
            <p className="text-[16px] text-[var(--body)] leading-relaxed max-w-md">A water purification system removes harmful contaminants and gives you peace of mind knowing your family has access to clean, great-tasting water every day.</p>
            <Btn to="/products" testId="learn-cta-btn" className="mt-6">Explore Our Products</Btn>
          </Reveal>
        </div>
      </section>
    </>
  );
}
